import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Plus, Trash2, Globe, CheckCircle, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function Sources() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    url: "",
    sitemap_url: "",
    active: true
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchSources();
  }, []);

  const fetchSources = async () => {
    try {
      const response = await axios.get(`${API}/sources`);
      setSources(response.data);
    } catch (error) {
      console.error("Erreur chargement sources:", error);
      toast.error("Erreur de chargement des sources");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/sources`, formData);
      toast.success("Source ajoutée avec succès !");
      setShowAddModal(false);
      setFormData({ name: "", url: "", sitemap_url: "", active: true });
      fetchSources();
    } catch (error) {
      console.error("Erreur ajout source:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (sourceId) => {
    if (!window.confirm("Voulez-vous vraiment supprimer cette source ?")) return;
    
    try {
      await axios.delete(`${API}/sources/${sourceId}`);
      toast.success("Source supprimée");
      fetchSources();
    } catch (error) {
      console.error("Erreur suppression:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="sources-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="sources-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Sources</h1>
          <p className="text-gray-600">Gérez les sites web à surveiller</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center space-x-2"
          data-testid="add-source-button"
        >
          <Plus size={20} />
          <span>Ajouter une source</span>
        </button>
      </div>

      {sources.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center" data-testid="no-sources">
          <Globe className="mx-auto text-gray-300 mb-4" size={48} />
          <p className="text-gray-600 mb-4">Aucune source configurée</p>
          <button onClick={() => setShowAddModal(true)} className="btn-primary">
            Ajouter votre première source
          </button>
        </div>
      ) : (
        <div className="table-container" data-testid="sources-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-header text-left">Nom</th>
                  <th className="table-header text-left">URL</th>
                  <th className="table-header text-left">Statut</th>
                  <th className="table-header text-left">Dernier scan</th>
                  <th className="table-header text-left">URLs détectées</th>
                  <th className="table-header text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((source) => (
                  <tr key={source.id} className="table-row" data-testid={`source-${source.id}`}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{source.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#4F86EF] hover:underline"
                      >
                        {source.url}
                      </a>
                    </td>
                    <td className="px-6 py-4">
                      {source.active ? (
                        <span className="badge badge-success flex items-center space-x-1 w-fit">
                          <CheckCircle size={14} />
                          <span>Actif</span>
                        </span>
                      ) : (
                        <span className="badge badge-default flex items-center space-x-1 w-fit">
                          <XCircle size={14} />
                          <span>Inactif</span>
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {source.last_check
                        ? new Date(source.last_check).toLocaleDateString('fr-FR')
                        : 'Jamais'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <span className="font-medium text-gray-900">{source.total_urls_found}</span>
                        <span className="text-gray-400 ml-1">totales</span>
                        {source.new_urls_detected > 0 && (
                          <div className="text-xs text-[#4F86EF] font-medium mt-1">
                            +{source.new_urls_detected} nouvelles
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleDelete(source.id)}
                        className="text-red-600 hover:text-red-700 p-2 rounded hover:bg-red-50"
                        data-testid={`delete-source-${source.id}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Source Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="add-source-modal">
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full mx-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Ajouter une source</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nom de la source</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F86EF] focus:border-transparent"
                  placeholder="Ex: Prix Pose"
                  required
                  data-testid="source-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">URL du site</label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F86EF] focus:border-transparent"
                  placeholder="https://www.exemple.com"
                  required
                  data-testid="source-url-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL du sitemap (optionnel)
                </label>
                <input
                  type="url"
                  value={formData.sitemap_url}
                  onChange={(e) => setFormData({ ...formData, sitemap_url: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F86EF] focus:border-transparent"
                  placeholder="https://www.exemple.com/sitemap.xml"
                  data-testid="source-sitemap-input"
                />
                <p className="text-xs text-gray-500 mt-1">Laissez vide pour détection automatique</p>
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary flex-1"
                  data-testid="cancel-add-source"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary flex-1 disabled:opacity-50"
                  data-testid="submit-add-source"
                >
                  {submitting ? "Ajout..." : "Ajouter"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
